'use client'

import { Container } from '@/components/ui/container'
import { ImageHelper } from '@/lib/helpers/image.helper'
import Image from 'next/image'

const DEFAULT_GALLERY_IMAGES = [
  '/image/placeholder-gallery-1.jpg',
  '/image/placeholder-gallery-2.jpg',
  '/image/placeholder-gallery-3.jpg',
  '/image/placeholder-gallery-4.jpg',
  '/image/placeholder-gallery-5.jpg',
  '/image/placeholder-gallery-6.jpg',
]

interface GallerySectionProps {
  images?: string[]
}

export function GallerySection({ images }: GallerySectionProps) {
  const allImages = images?.length ? images : DEFAULT_GALLERY_IMAGES

  // Split images into 2 rows
  const halfLength = Math.ceil(allImages.length / 2)
  const row1Images = allImages.slice(0, halfLength)
  const row2Images = allImages.slice(halfLength)

  const renderImageRow = (
    rowImages: string[],
    rowIndex: number,
    isDuplicate = false
  ) => (
    <div className='flex gap-2 sm:gap-3 md:gap-4'>
      {rowImages.map((image, index) => (
        <div
          key={`row${rowIndex}-${isDuplicate ? 'dup-' : ''}${index}`}
          className='relative h-[120px] min-w-[160px] flex-shrink-0 overflow-hidden rounded-lg sm:h-[150px] sm:min-w-[200px] md:h-[180px] md:min-w-[240px] lg:h-[200px] lg:min-w-[280px]'
        >
          <Image
            src={ImageHelper.getImageUrl(image)}
            alt={`Gallery image ${rowIndex * halfLength + index + 1}`}
            fill
            className='object-cover'
            loading='lazy'
            sizes='(max-width: 640px) 160px, (max-width: 768px) 200px, (max-width: 1024px) 240px, 280px'
          />
        </div>
      ))}
    </div>
  )

  return (
    <div
      className='relative overflow-hidden bg-[#1a1a1a] py-8 md:py-10 lg:py-12'
      data-section='gallery'
    >
      <Container fluid={true} className='relative'>
        {/* Logo CXP - Top Left */}
        <div className='flex items-center gap-1.5 sm:gap-2 md:gap-3'>
          <div className='flex h-6 w-6 items-center justify-center sm:h-7 sm:w-7 md:h-8 md:w-8 lg:h-12 lg:w-12'>
            <Image
              src={ImageHelper.getN7ImageUrl('/image/2026/01/14/mceclip0.png')}
              alt='CXP Logo'
              width={24}
              height={24}
              className='h-full w-full object-contain'
            />
          </div>
          <div className='flex flex-col'>
            <span className='font-formaDjr text-base font-bold leading-none text-white sm:text-lg md:text-xl lg:text-2xl xl:text-3xl'>
              CXP
            </span>
            <span className='font-formaDjr text-[10px] leading-none text-white sm:text-xs md:text-sm'>
              by Coolmate
            </span>
          </div>
        </div>

        {/* Gallery with 2 rows and marquee scroll */}
        <div className='mt-6 flex flex-col gap-2 overflow-hidden sm:mt-8 sm:gap-3 md:mt-10 md:gap-4 lg:mt-12'>
          {/* Row 1 - scroll left */}
          <div className='gallery-marquee-container'>
            <div className='gallery-marquee-track'>
              <div className='gallery-marquee-content'>
                {renderImageRow(row1Images, 0)}
              </div>
              <div className='gallery-marquee-content'>
                {renderImageRow(row1Images, 0, true)}
              </div>
            </div>
          </div>

          {/* Row 2 - scroll right (reverse) */}
          <div className='gallery-marquee-container'>
            <div className='gallery-marquee-track-reverse'>
              <div className='gallery-marquee-content'>
                {renderImageRow(row2Images, 1)}
              </div>
              <div className='gallery-marquee-content'>
                {renderImageRow(row2Images, 1, true)}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}
